import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Badge, Accordion } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faEdit,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	faPlus,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	faTrash,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	faUser,
	faCalendar,
	faClock,
	faMapMarkerAlt,
	faUsers,
	faFileAlt,
	faBell,
	faImage,
} from "@fortawesome/free-solid-svg-icons";
import "./EventChangelog.css";

export interface ChangelogEntry {
	id: string;
	timestamp: string;
	user: string;
	action: string;
	field: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	oldValue?: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	newValue?: any;
	description?: string;
}

export interface EventChangelogProps {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	eventData: any;
	onValueChange?: (changelog: ChangelogEntry[]) => void;
}

const EventChangelog: React.FC<EventChangelogProps> = ({
	eventData,
	onValueChange,
}) => {
	const [changelog, setChangelog] = useState<ChangelogEntry[]>([]);
	const [parsedChangelog, setParsedChangelog] = useState<ChangelogEntry[]>([]);

	// Parse individual change line from the changelog format
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const parseChangeLine = (changeLine: string) => {
		// Remove the leading "- " and trim
		const cleanLine = changeLine.replace(/^-\s*/, "").trim();

		// Parse different types of changes

		// Pattern: "Changed Field from OldValue to NewValue"
		const changedMatch = cleanLine.match(
			/^Changed\s+([^.]+(?:\.[^.]+)*)\s+from\s+(.+?)\s+to\s+(.+)$/
		);
		if (changedMatch) {
			return {
				action: "changed",
				field: changedMatch[1].trim(),
				oldValue: parseValue(changedMatch[2].trim()),
				newValue: parseValue(changedMatch[3].trim()),
				description: cleanLine,
			};
		}

		// Pattern: "Added Field: Value"
		const addedMatch = cleanLine.match(/^Added\s+([^.]+(?:\.[^.]+)*):\s*(.+)$/);
		if (addedMatch) {
			return {
				action: "added",
				field: addedMatch[1].trim(),
				oldValue: undefined,
				newValue: parseValue(addedMatch[2].trim()),
				description: cleanLine,
			};
		}

		// Pattern: "Removed Field: Value"
		const removedMatch = cleanLine.match(
			/^Removed\s+([^.]+(?:\.[^.]+)*):\s*(.+)$/
		);
		if (removedMatch) {
			return {
				action: "removed",
				field: removedMatch[1].trim(),
				oldValue: parseValue(removedMatch[2].trim()),
				newValue: undefined,
				description: cleanLine,
			};
		}

		// Pattern: "Changed Field structure: Description"
		const structureMatch = cleanLine.match(
			/^Changed\s+([^.]+(?:\.[^.]+)*)\s+structure:\s*(.+)$/
		);
		if (structureMatch) {
			return {
				action: "restructured",
				field: structureMatch[1].trim(),
				oldValue: undefined,
				newValue: undefined,
				description: cleanLine,
			};
		}

		// If no pattern matches, treat as a general change
		return {
			action: "modified",
			field: "unknown",
			oldValue: undefined,
			newValue: undefined,
			description: cleanLine,
		};
	};

	// Parse value strings to appropriate types
	const parseValue = (valueStr: string) => {
		// Handle None/null values
		if (valueStr === "None" || valueStr === "null") {
			return null;
		}

		// Handle boolean values
		if (valueStr === "True" || valueStr === "true") {
			return true;
		}
		if (valueStr === "False" || valueStr === "false") {
			return false;
		}

		// Handle numbers
		if (!isNaN(Number(valueStr)) && valueStr.trim() !== "") {
			return Number(valueStr);
		}

		// Handle arrays
		if (valueStr === "[]") {
			return [];
		}

		// Handle objects (dictionary-like strings)
		if (valueStr.startsWith("{") && valueStr.endsWith("}")) {
			try {
				// Replace single quotes with double quotes for JSON parsing
				const jsonStr = valueStr.replace(/'/g, '"');
				return JSON.parse(jsonStr);
			} catch {
				return valueStr; // Return as string if parsing fails
			}
		}

		// Handle quoted strings
		if (
			(valueStr.startsWith("'") && valueStr.endsWith("'")) ||
			(valueStr.startsWith('"') && valueStr.endsWith('"'))
		) {
			return valueStr.slice(1, -1);
		}

		// Return as string
		return valueStr;
	};

	// Parse event data to extract changelog information
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const parseEventData = (data: any): ChangelogEntry[] => {
		const entries: ChangelogEntry[] = [];

		if (!data) return entries;

		// Check for ChangeLog array (your specific format)
		if (data.ChangeLog && Array.isArray(data.ChangeLog)) {
			const allEntries: ChangelogEntry[] = [];

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			data.ChangeLog.forEach((entry: any, entryIndex: number) => {
				if (entry.Changes && typeof entry.Changes === "string") {
					// Create a single entry for each ChangeLog item
					allEntries.push({
						id: `changelog-${entryIndex}`,
						timestamp:
							entry.UpdatedAt || entry.timestamp || new Date().toISOString(),
						user: entry.UpdatedBy?.Name || "System",
						action: "updated",
						field: "event",
						oldValue: undefined,
						newValue: undefined,
						description: entry.Changes, // Return the Changes value as a string
					});
				}
			});

			return allEntries;
		}

		// For now, only handle the ChangeLog format
		// Other formats can be added back if needed
		return entries.sort(
			(a, b) =>
				new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
		);
	};

	// Get icon for different field types
	const getFieldIcon = (field: string) => {
		const fieldLower = field.toLowerCase();

		if (fieldLower.includes("title") || fieldLower.includes("name"))
			return faEdit;
		if (fieldLower.includes("date") || fieldLower.includes("time"))
			return faCalendar;
		if (fieldLower.includes("location") || fieldLower.includes("address"))
			return faMapMarkerAlt;
		if (fieldLower.includes("group") || fieldLower.includes("attendee"))
			return faUsers;
		if (fieldLower.includes("form") || fieldLower.includes("permission"))
			return faFileAlt;
		if (fieldLower.includes("notice") || fieldLower.includes("notification"))
			return faBell;
		if (fieldLower.includes("banner") || fieldLower.includes("image"))
			return faImage;

		return faEdit;
	};

	// Get action badge variant
	const getActionVariant = (action: string) => {
		const actionLower = action.toLowerCase();

		if (
			actionLower.includes("create") ||
			actionLower.includes("add") ||
			actionLower === "added"
		)
			return "success";
		if (
			actionLower.includes("delete") ||
			actionLower.includes("remove") ||
			actionLower === "removed"
		)
			return "danger";
		if (
			actionLower.includes("update") ||
			actionLower.includes("modify") ||
			actionLower === "changed"
		)
			return "primary";
		if (actionLower.includes("activate")) return "warning";
		if (actionLower === "restructured") return "info";

		return "secondary";
	};

	// Format timestamp
	const formatTimestamp = (timestamp: string) => {
		try {
			const date = new Date(timestamp);
			return date.toLocaleString("en-US", {
				year: "numeric",
				month: "short",
				day: "numeric",
				hour: "2-digit",
				minute: "2-digit",
			});
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error) {
			return timestamp;
		}
	};

	// Format value for display
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const formatValue = (value: any): string => {
		if (value === null || value === undefined) return "None";
		if (typeof value === "boolean") return value ? "Yes" : "No";
		if (typeof value === "object") {
			if (Array.isArray(value)) {
				return value.length > 0 ? `${value.length} items` : "Empty";
			}
			return JSON.stringify(value);
		}
		return String(value);
	};

	useEffect(() => {
		if (eventData) {
			console.log("Event Data", eventData);
			const parsed = parseEventData(eventData);
			setParsedChangelog(parsed);
			setChangelog(parsed);
		}
	}, [eventData]);

	useEffect(() => {
		if (onValueChange) {
			onValueChange(changelog);
		}
	}, [changelog, onValueChange]);

	if (!eventData) {
		return (
			<Container>
				<Row>
					<Col>
						<Card>
							<Card.Body>
								<p className="text-muted">
									No event data available to display changelog.
								</p>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</Container>
		);
	}

	if (parsedChangelog.length === 0) {
		return (
			<Container>
				<Row>
					<Col>
						<Card>
							<Card.Body>
								<p className="text-muted">
									No changelog data available for this event.
								</p>
								<small>
									This could mean:
									<ul>
										<li>The event hasn't been modified yet</li>
										<li>Changelog tracking is not enabled</li>
										<li>The changelog data is stored in a different format</li>
									</ul>
								</small>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</Container>
		);
	}

	return (
		<Container>
			<Row>
				<Col>
					<Card>
						<Card.Header>
							<h5 className="mb-0">
								<FontAwesomeIcon icon={faClock} className="me-2" />
								Event Change History
							</h5>
						</Card.Header>
						<Card.Body>
							<Accordion>
								{parsedChangelog.map((entry, index) => (
									<Accordion.Item key={entry.id} eventKey={index.toString()}>
										<Accordion.Header>
											<Row className="w-100 align-items-center">
												<Col xs="auto">
													<FontAwesomeIcon
														icon={getFieldIcon(entry.field)}
														className="me-2"
													/>
												</Col>
												<Col>
													<strong>{entry.field}</strong>
													<Badge
														bg={getActionVariant(entry.action)}
														className="ms-2"
													>
														{entry.action}
													</Badge>
												</Col>
												<Col xs="auto">
													<small className="text-muted">
														{formatTimestamp(entry.timestamp)}
													</small>
												</Col>
											</Row>
										</Accordion.Header>
										<Accordion.Body>
											<Row>
												<Col md={12}>
													<strong>User:</strong> {entry.user}
												</Col>
											</Row>
											{entry.description && (
												<Row className="mt-2">
													<Col>
														{entry.description
															?.split("\n\n")[0]
															.split("\n")
															.map((line, i) => (
																<React.Fragment key={i}>
																	{line}
																	<br />
																</React.Fragment>
															))}
													</Col>
												</Row>
											)}
											{(entry.oldValue !== undefined ||
												entry.newValue !== undefined) && (
												<Row className="mt-2">
													<Col md={6}>
														<strong>Previous Value:</strong>
														<div className="text-muted">
															{formatValue(entry.oldValue)}
														</div>
													</Col>
													<Col md={6}>
														<strong>New Value:</strong>
														<div className="text-success">
															{formatValue(entry.newValue)}
														</div>
													</Col>
												</Row>
											)}
										</Accordion.Body>
									</Accordion.Item>
								))}
							</Accordion>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</Container>
	);
};
export default EventChangelog;
